import allLocales from "@fullcalendar/core/locales-all";
import dayGridPlugin from "@fullcalendar/daygrid";
import FullCalendar from "@fullcalendar/react";
import { useToastContext } from "hooks/useToastContext";
import { getLocaleCode } from "locales";
import { type ChangeEvent, useMemo, useRef, useState } from "react";
import intl from "react-intl-universal";
import type { ICapacityByDayDto } from "types/capacity";
import { useGridParameterizationContext } from "../../context";
import { isFilterSelected } from "../../utils";
import ModalEditMultipleDates from "./components/modal-edit-multiple-dates";
import { CalendarContainer, DayCellCustom } from "./styled";

interface IGridParametrizationCalendarProps {
  type: string;
  capacityByDay: ICapacityByDayDto[];
  handleInputCellChange: (date: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  disableChangeMonth: boolean;
  handleGetCalendarDataAsync: () => Promise<void>;
}

const GridParameterizationCalendar = ({
  type,
  capacityByDay,
  handleInputCellChange,
  disableChangeMonth,
  handleGetCalendarDataAsync,
}: IGridParametrizationCalendarProps) => {
  const calendarRef = useRef<FullCalendar>(null);
  const { openToast } = useToastContext();
  const {
    filtersLoadCapacity,
    filtersUnloadCapacity,
    selectedRadioUnloadCapacity,
    calendarDateView,
    setCalendarDateView,
  } = useGridParameterizationContext();
  const today = new Date();
  const [isCurrentMonth, setIsCurrentMonth] = useState(true);
  const [isAtStartLimit, setIsAtStartLimit] = useState(false);
  const [isAtEndLimit, setIsAtEndLimit] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [showModalEditMultipleDates, setShowModalEditMultipleDates] = useState(false);
  const { startDate, endDate } = useMemo(() => {
    const _startDate = new Date(today);
    _startDate.setFullYear(today.getFullYear() - 1, today.getMonth(), 1);
    const _endDate = new Date(today);
    _endDate.setFullYear(today.getFullYear() + 1, today.getMonth() + 1, 0);

    return {
      startDate: _startDate,
      endDate: _endDate,
    };
  }, [today]);

  const getDayCapacity = (date: string) => {
    const _capacity = capacityByDay.find((x) => x.Date === date)?.Capacity;
    return _capacity?.toString() ?? "";
  };

  const getDayBlocked = (date: string) => {
    const _blocked = capacityByDay.find((x) => x.Date === date)?.Blocked;
    return _blocked ?? false;
  };

  const handleInputChange = (date: string, event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === "" || (/^\d+$/.test(value) && Number(value) <= 1000)) {
      handleInputCellChange(date, event);
    }
  };

  const handleDisabledWarning = (
    isDateLessThanToday: boolean,
    isDateNotOnCurrentMonth: boolean,
    noFilter: boolean
  ) => {
    if (noFilter) {
      openToast(
        intl.get("master-data.managementUnity.grid.general-warning-messages.provide-a-filter"),
        "warning"
      );
      return;
    }

    if (isDateLessThanToday) {
      openToast(
        intl.get(
          "master-data.managementUnity.grid.general-warning-messages.not-allowed-edit-data-prior-today"
        ),
        "warning"
      );
      return;
    }

    if (isDateNotOnCurrentMonth) {
      openToast(
        intl.get(
          "master-data.managementUnity.grid.general-warning-messages.changes-monthly-go-to-next-month"
        ),
        "warning"
      );
    }
  };

  const renderDayCellContent = (arg: {
    date: Date;
    dayNumberText: string;
    isPast: boolean;
    isOther: boolean;
  }) => {
    const date = arg.date.toISOString().split("T")[0]; // Get date in YYYY-MM-DD format
    const hasntFilterSelected = !isFilterSelected(
      type,
      filtersLoadCapacity,
      filtersUnloadCapacity,
      selectedRadioUnloadCapacity
    );
    const isInputDisabled = arg.isPast || arg.isOther || hasntFilterSelected;
    setIsFiltered(!hasntFilterSelected);

    const isBlocked = getDayBlocked(date);

    return (
      <DayCellCustom
        isPastDate={arg.isPast}
        isOtherMonth={arg.isOther}
        isFiltered={!hasntFilterSelected}
        isBlocked={isBlocked}
        className={`custom-day-cell ${isBlocked ? "blocked-day" : ""}`}
        onClick={() => {
          if (isInputDisabled) handleDisabledWarning(arg.isPast, arg.isOther, hasntFilterSelected);
        }}
      >
        <div className="day-number">{arg.dayNumberText}</div>
        <div className="input-wrapper">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={getDayCapacity(date)}
            onChange={(event: ChangeEvent<HTMLInputElement>) => handleInputChange(date, event)}
            className="input-day-cell"
            disabled={isInputDisabled}
            maxLength={4}
          />
        </div>
      </DayCellCustom>
    );
  };

  const handleCustomPrevYearClick = () => {
    if (calendarRef.current && !isAtStartLimit && !disableChangeMonth) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.prevYear();
    }
  };

  const handleCustomPrevClick = () => {
    if (calendarRef.current && !isAtStartLimit && !disableChangeMonth) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.prev();
    }
  };

  const handleCustomNextClick = () => {
    if (calendarRef.current && !isAtEndLimit && !disableChangeMonth) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.next();
    }
  };

  const handleCustomNextYearClick = () => {
    if (calendarRef.current && !isAtEndLimit && !disableChangeMonth) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.nextYear();
    }
  };

  const handleCustomTodayClick = () => {
    if (calendarRef.current && !isCurrentMonth && !disableChangeMonth) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.today();
    }
  };

  const handleCustomEditMultipleDates = () => {
    setShowModalEditMultipleDates((prevState) => !prevState);
  };

  const handleDatesSet = (dateInfo: {
    startStr: string;
    endStr: string;
    view: { currentStart: Date };
  }) => {
    const currentDate = dateInfo.view.currentStart;

    const isSameMonth =
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear();

    setIsCurrentMonth(isSameMonth);

    // Verificar se está no limite inicial (startDate)
    const isAtStart =
      currentDate.getMonth() === startDate.getMonth() &&
      currentDate.getFullYear() === startDate.getFullYear();

    setIsAtStartLimit(isAtStart);

    // Verificar se está no limite final (endDate)
    const isAtEnd =
      currentDate.getMonth() === endDate.getMonth() &&
      currentDate.getFullYear() === endDate.getFullYear();

    setIsAtEndLimit(isAtEnd);

    if (
      dateInfo.startStr !== calendarDateView.startDate &&
      dateInfo.endStr !== calendarDateView.endDate
    ) {
      setCalendarDateView({
        startDate: dateInfo.startStr,
        endDate: dateInfo.endStr,
      });
    }
  };

  return (
    <>
      <CalendarContainer
        isCurrentMonth={isCurrentMonth}
        isAtStartLimit={isAtStartLimit}
        isAtEndLimit={isAtEndLimit}
        isChangeMonthDisabled={disableChangeMonth}
        isFiltered={isFiltered}
      >
        <FullCalendar
          ref={calendarRef}
          locales={allLocales}
          locale={getLocaleCode()}
          plugins={[dayGridPlugin]}
          weekends={true}
          initialView="dayGridMonth"
          validRange={{
            start: startDate,
            end: endDate,
          }}
          headerToolbar={{
            left: "title",
            center: "",
            right:
              "customPrevYear,customPrev,customNext,customNextYear customToday customEditMultipleDates",
          }}
          dayCellContent={renderDayCellContent}
          dayHeaderFormat={{ weekday: "long" }}
          datesSet={handleDatesSet}
          customButtons={{
            customPrevYear: {
              icon: "chevrons-left",
              click: handleCustomPrevYearClick,
            },
            customPrev: {
              icon: "chevron-left",
              click: handleCustomPrevClick,
            },
            customNext: {
              icon: "chevron-right",
              click: handleCustomNextClick,
            },
            customNextYear: {
              icon: "chevrons-right",
              click: handleCustomNextYearClick,
            },
            customToday: {
              text: intl.get("weekdays.today"),
              click: handleCustomTodayClick,
              hint: intl.get("weekdays.today"),
            },
            customEditMultipleDates: {
              text: intl.get("master-data.managementUnity.grid.generalActions.editMultipleDates"),
              click: handleCustomEditMultipleDates,
              themeIcon: "edit-button",
            },
          }}
        />
      </CalendarContainer>

      <ModalEditMultipleDates
        type={type}
        open={showModalEditMultipleDates}
        handleClose={() => setShowModalEditMultipleDates(false)}
        calendarEndDate={endDate}
        handleGetCalendarDataAsync={handleGetCalendarDataAsync}
      />
    </>
  );
};

export default GridParameterizationCalendar;
